import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import type { DetailedFollowUpTicket } from 'src/types/followUp';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import useTicketDetailQuery from 'src/components/followUp/hooks/useTicketDetailQuery';

const useTicketStatusUpdate = ({
  ticket,
  refetchList,
}: {
  ticket?: DetailedFollowUpTicket;
  refetchList: () => void;
}) => {
  const { t } = useTranslation('followUp');
  const [snackbar, setSnackbar] = useState<{
    title: string;
    success: boolean;
    message?: string;
  } | null>(null);
  const { refetch: refreshSelectedTicket } = useTicketDetailQuery(ticket?.id);

  const closeTicketEndpoint = useMemo(
    () =>
      generatePath(apiPaths.FOLLOW_UP.TICKETS.CLOSE_TICKET, {
        id: ticket?.id,
      }),
    [ticket?.id],
  );
  const { mutate: closeTicket, isLoading: isClosing } = useMutation(
    [closeTicketEndpoint],
    async () => {
      if (!ticket) return;

      if (ticket.npsGroup === 'Detractor' && !ticket.followUpCall) {
        // The evaluation warrants a follow-up call and one wasn't made yet.
        return Promise.reject(t('makeCallFirst'));
      }

      await HttpService.put(closeTicketEndpoint)
        .then((data) => Promise.resolve(data.data))
        .catch(({ response }) => Promise.reject(response.status));
    },
    {
      onSuccess: () => {
        setSnackbar({
          title: t('ticketClosed'),
          success: true,
        });
        refetchList();
        refreshSelectedTicket();
      },
      onError: (error: string) =>
        setSnackbar({
          title: t('ticketNotClosed'),
          message: error,
          success: false,
        }),
    },
  );

  const reopenTicketEndpoint = useMemo(
    () =>
      generatePath(apiPaths.FOLLOW_UP.TICKETS.REOPEN_TICKET, {
        id: ticket?.id,
      }),
    [ticket?.id],
  );
  const { mutate: reopenTicket, isLoading: isOpening } = useMutation(
    [reopenTicketEndpoint],
    async () => {
      if (!ticket) return;
      await HttpService.put(reopenTicketEndpoint)
        .then((data) => Promise.resolve(data.data))
        .catch(({ response }) => Promise.reject(response.status));
    },
    {
      onSuccess: () => {
        setSnackbar({
          title: t('ticketReopened'),
          success: true,
        });
        refetchList();
        refreshSelectedTicket();
      },
      onError: (error: string) =>
        setSnackbar({
          title: t('ticketNotReopened'),
          message: error,
          success: false,
        }),
    },
  );

  return {
    reopenTicket,
    closeTicket,
    snackbar,
    setSnackbar,
    isUpdating: isClosing || isOpening,
  };
};

export default useTicketStatusUpdate;
